import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, ExternalLink, FileText, Pin, PinOff, RefreshCw, Rocket, X } from 'lucide-react'
import { Badge, Button } from '@goodday-clone/ui'
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog'
import { api } from '@/api/client'
import { groupServicesByCategory } from '@/shared/constants/services'
import type { DealStageOption } from '@/shared/hooks/useCrmVocabulary'
import type { LinkedProjectInfo } from '@/lib/deal-project-utils'
import {
  PAGE_SIZES,
  describeBulkResult,
  paginate,
  parseCloseDate,
  runBulk,
  sortDeals,
  type DealSort,
  type DealSortKey,
} from '@/lib/dealsTable'
import { badgeStyle, rupees } from '@/features/dashboard/utils/format'
import type { Deal } from '@/pages/DealsPage'
import { quotationViewPath } from '@/components/sales/DealQuotationLink'
import { cn, cardClassName } from '@jira-clone/ui'

export const DEAL_PRIORITIES = ['Highest', 'High', 'Normal', 'Low', 'Lowest']

interface AssignableUser {
  id: string
  name: string
}

interface DealsTableProps {
  deals: Deal[]
  stages: DealStageOption[]
  assignableUsers: AssignableUser[]
  isDealWon: (stage?: string) => boolean
  linkedProjects: Map<string, LinkedProjectInfo>
  canCreateSpace: boolean
  canViewSpace: boolean
  creatingDealId: string | null
  onOpen: (deal: Deal) => void
  onCreateQuotation: (deal: Deal) => void
  onCreateSpace: (deal: Deal) => void
  /** Called with what changed locally after a bulk update, so the page can refresh its list without refetching. */
  onDealsChanged: (changes: Array<{ id: string; changes: Partial<Deal> }>) => void
  /** Changes whenever the filters do: back to page 1 and clear the selection. */
  resetKey: string
}

type PendingBulk =
  | { kind: 'stage'; value: string; label: string }
  | { kind: 'owner'; value: string; label: string }
  | { kind: 'priority'; value: string; label: string }

const COLUMNS: Array<{ key: DealSortKey | null; label: string; align?: 'right' }> = [
  { key: 'title', label: 'Deal' },
  { key: 'stage', label: 'Stage' },
  { key: 'value', label: 'Value', align: 'right' },
  { key: 'probability', label: 'Win %' },
  { key: 'owner', label: 'Owner' },
  { key: null, label: 'Priority' },
  { key: null, label: 'Services' },
  { key: 'closeDate', label: 'Target' },
]

const selectClass =
  'rounded-lg border border-border/80 bg-background px-2 py-1 text-xs font-medium text-foreground shadow-2xs focus:outline-none focus:ring-2 focus:ring-primary/30 cursor-pointer'

export function DealsTable({
  deals,
  stages,
  assignableUsers,
  isDealWon,
  linkedProjects,
  canCreateSpace,
  canViewSpace,
  creatingDealId,
  onOpen,
  onCreateQuotation,
  onCreateSpace,
  onDealsChanged,
  resetKey,
}: DealsTableProps) {
  const [sort, setSort] = useState<DealSort | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<number>(25)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [pending, setPending] = useState<PendingBulk | null>(null)
  const [busy, setBusy] = useState(false)

  // New filters: start again from the first page with nothing selected.
  useEffect(() => {
    setPage(1)
    setSelected(new Set())
  }, [resetKey])

  // Drop selected deals that are no longer in the list (filtered out or deleted).
  useEffect(() => {
    setSelected((prev) => {
      const present = new Set(deals.map((d) => d.id))
      const next = new Set(Array.from(prev).filter((id) => present.has(id)))
      return next.size === prev.size ? prev : next
    })
  }, [deals])

  const sorted = useMemo(() => sortDeals(deals, sort), [deals, sort])
  const paged = useMemo(() => paginate(sorted, page, pageSize), [sorted, page, pageSize])
  const pageIds = paged.rows.map((d) => d.id)
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id))
  const someOnPageSelected = pageIds.some((id) => selected.has(id))

  const headerCheckbox = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (headerCheckbox.current) headerCheckbox.current.indeterminate = someOnPageSelected && !allOnPageSelected
  }, [someOnPageSelected, allOnPageSelected])

  const stageByName = useMemo(() => new Map(stages.map((s) => [s.name.toLowerCase(), s])), [stages])
  const startOfToday = useMemo(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
  }, [])

  const toggleSort = (key: DealSortKey) => {
    setSort((prev) => (prev?.key !== key ? { key, dir: 'asc' } : prev.dir === 'asc' ? { key, dir: 'desc' } : null))
    setPage(1)
  }

  const toggleRow = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const togglePage = () =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (allOnPageSelected) pageIds.forEach((id) => next.delete(id))
      else pageIds.forEach((id) => next.add(id))
      return next
    })

  const selectedIds = Array.from(selected)

  // ---- bulk updates: one request per deal, so the same rules apply as editing one deal ----
  const applyBulk = async (what: string, worker: (id: string) => Promise<unknown>, changes: Partial<Deal>) => {
    setBusy(true)
    try {
      const result = await runBulk(selectedIds, worker)
      if (result.updated.length > 0) {
        onDealsChanged(result.updated.map((id) => ({ id, changes })))
      }
      const { message, ok } = describeBulkResult(result, what)
      if (ok) toast.success(message)
      else if (result.updated.length > 0) toast(message, { icon: '⚠️' })
      else toast.error(message)
      setSelected(new Set())
    } finally {
      setBusy(false)
      setPending(null)
    }
  }

  const confirmPending = () => {
    if (!pending) return
    if (pending.kind === 'stage') {
      return applyBulk('Stage', (id) => api.patch(`/deals/${id}`, { stage: pending.value }, { skipErrorToast: true }), { stage: pending.value })
    }
    if (pending.kind === 'priority') {
      return applyBulk('Priority', (id) => api.patch(`/deals/${id}`, { priority: pending.value }, { skipErrorToast: true }), { priority: pending.value })
    }
    return applyBulk(
      'Owner',
      (id) => api.patch(`/deals/${id}`, { assignedToUserId: pending.value, owner: pending.label }, { skipErrorToast: true }),
      { owner: pending.label }
    )
  }

  const setPinned = (pinned: boolean) =>
    applyBulk(pinned ? 'Pinned' : 'Unpinned', (id) => api.patch(`/deals/${id}/pin`, { pinned }, { skipErrorToast: true }), { pinned })

  const pendingText = pending
    ? pending.kind === 'stage'
      ? `Move ${selectedIds.length} ${selectedIds.length === 1 ? 'deal' : 'deals'} to "${pending.label}"?`
      : pending.kind === 'owner'
        ? `Reassign ${selectedIds.length} ${selectedIds.length === 1 ? 'deal' : 'deals'} to ${pending.label}?`
        : `Set the priority of ${selectedIds.length} ${selectedIds.length === 1 ? 'deal' : 'deals'} to ${pending.label}?`
    : ''

  return (
    <div className="space-y-3">
      {selected.size > 0 && (
        <div
          role="toolbar"
          aria-label="Bulk actions"
          className="flex flex-wrap items-center gap-2 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2"
        >
          <span className="text-xs font-semibold text-foreground">{selected.size} selected</span>
          {allOnPageSelected && deals.length > paged.rows.length && selected.size < deals.length && (
            <button
              type="button"
              onClick={() => setSelected(new Set(deals.map((d) => d.id)))}
              className="text-xs font-medium text-primary hover:underline"
            >
              Select all {deals.length}
            </button>
          )}

          <select
            aria-label="Change stage"
            className={selectClass}
            disabled={busy}
            value=""
            onChange={(e) => e.target.value && setPending({ kind: 'stage', value: e.target.value, label: e.target.value })}
          >
            <option value="">Change stage…</option>
            {stages.filter((s) => !s.isWon).map((s) => (
              <option key={s.id ?? s.name} value={s.name}>
                {s.name}
              </option>
            ))}
          </select>

          <select
            aria-label="Change owner"
            className={selectClass}
            disabled={busy}
            value=""
            onChange={(e) => {
              const user = assignableUsers.find((u) => u.id === e.target.value)
              if (user) setPending({ kind: 'owner', value: user.id, label: user.name })
            }}
          >
            <option value="">Change owner…</option>
            {assignableUsers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>

          <select
            aria-label="Change priority"
            className={selectClass}
            disabled={busy}
            value=""
            onChange={(e) => e.target.value && setPending({ kind: 'priority', value: e.target.value, label: e.target.value })}
          >
            <option value="">Change priority…</option>
            {DEAL_PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>

          <Button size="sm" variant="outline" disabled={busy} onClick={() => setPinned(true)} className="h-7 gap-1 text-xs">
            <Pin className="h-3.5 w-3.5" /> Pin
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => setPinned(false)} className="h-7 gap-1 text-xs">
            <PinOff className="h-3.5 w-3.5" /> Unpin
          </Button>

          <button
            type="button"
            onClick={() => setSelected(new Set())}
            className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" /> Clear
          </button>
        </div>
      )}

      <div className={cn(cardClassName(), 'overflow-x-auto')}>
        <table className="w-full text-left text-sm">
          <thead className="border-b border-border/50 bg-muted/40 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="w-10 px-3 py-3">
                <input
                  ref={headerCheckbox}
                  type="checkbox"
                  aria-label="Select all deals on this page"
                  checked={allOnPageSelected}
                  onChange={togglePage}
                  className="h-4 w-4 cursor-pointer rounded border-input"
                />
              </th>
              {COLUMNS.map((col) => {
                const active = col.key !== null && sort?.key === col.key
                return (
                  <th
                    key={col.label}
                    scope="col"
                    aria-sort={active ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : col.key ? 'none' : undefined}
                    className={`px-3 py-3 ${col.align === 'right' ? 'text-right' : ''}`}
                  >
                    {col.key ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(col.key!)}
                        className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-foreground ${active ? 'text-foreground' : ''}`}
                      >
                        {col.label}
                        {active && (sort!.dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                      </button>
                    ) : (
                      col.label
                    )}
                  </th>
                )
              })}
              <th scope="col" className="px-3 py-3 text-right">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/40">
            {paged.rows.map((deal) => {
              const stage = stageByName.get((deal.stage || '').toLowerCase())
              const won = isDealWon(deal.stage)
              const target = parseCloseDate(deal.closeDate)
              const overdue = !!target && target < startOfToday && !won && !stage?.isLost
              const linked = linkedProjects.get(deal.id)
              const isSelected = selected.has(deal.id)
              const stageStyle = badgeStyle(stage?.color)
              const categories = groupServicesByCategory(deal.services ?? [])

              return (
                <tr
                  key={deal.id}
                  onClick={() => onOpen(deal)}
                  className={`cursor-pointer transition-colors hover:bg-muted/40 ${isSelected ? 'bg-primary/5' : ''}`}
                >
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={`Select ${deal.title}`}
                      checked={isSelected}
                      onChange={() => toggleRow(deal.id)}
                      className="h-4 w-4 cursor-pointer rounded border-input"
                    />
                  </td>
                  <td className="max-w-[260px] px-3 py-3">
                    <div className="flex items-center gap-1.5">
                      {deal.pinned && <Pin className="h-3.5 w-3.5 shrink-0 text-amber-500" aria-label="Pinned" />}
                      <span className="truncate font-semibold text-foreground" title={deal.title}>
                        {deal.title}
                      </span>
                    </div>
                    <p className="truncate text-xs text-muted-foreground">{deal.client}</p>
                  </td>
                  <td className="px-3 py-3">
                    {stageStyle ? (
                      <span
                        style={stageStyle}
                        className="inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold"
                      >
                        {deal.stage}
                      </span>
                    ) : (
                      <Badge variant="outline" className="whitespace-nowrap text-[11px]">
                        {deal.stage}
                      </Badge>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right font-semibold text-foreground">{rupees(deal.value || 0)}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-14 overflow-hidden rounded-full bg-muted">
                        <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, Math.max(0, deal.probability || 0))}%` }} />
                      </div>
                      <span className="text-xs font-medium text-muted-foreground">{deal.probability}%</span>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-xs text-muted-foreground">{deal.owner || 'Unassigned'}</td>
                  <td className="px-3 py-3">
                    {deal.priority && (
                      <span className="rounded bg-muted/60 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">{deal.priority}</span>
                    )}
                  </td>
                  <td className="max-w-[220px] px-3 py-3">
                    <div className="flex flex-wrap gap-1">
                      {categories.slice(0, 2).map(({ category, services: svcs }) => (
                        <span
                          key={category.category}
                          title={`${category.category}: ${svcs.join(', ')}`}
                          className={`max-w-[120px] truncate rounded border px-1.5 py-0.5 text-[10px] font-medium ${category.badgeClass}`}
                        >
                          {svcs[0]}
                          {svcs.length > 1 ? ` +${svcs.length - 1}` : ''}
                        </span>
                      ))}
                      {categories.length > 2 && (
                        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground" title={categories.slice(2).map((c) => c.category.category).join(', ')}>
                          +{categories.length - 2}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className={`whitespace-nowrap px-3 py-3 text-xs ${overdue ? 'font-semibold text-red-600 dark:text-red-400' : 'text-muted-foreground'}`}>
                    {deal.closeDate || '—'}
                    {overdue && <span className="sr-only"> (overdue)</span>}
                  </td>
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      {deal.quotation ? (
                        <Link
                          to={quotationViewPath(deal.quotation)}
                          title={`View quotation ${deal.quotation.number}`}
                          aria-label={`View quotation ${deal.quotation.number} for ${deal.title}`}
                          className="rounded-md p-1.5 text-emerald-600 transition-colors hover:bg-emerald-50 dark:hover:bg-emerald-950/40"
                        >
                          <FileText className="h-4 w-4" />
                        </Link>
                      ) : (
                        <button
                          type="button"
                          title="Create quotation"
                          aria-label={`Create quotation for ${deal.title}`}
                          onClick={() => onCreateQuotation(deal)}
                          className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        >
                          <FileText className="h-4 w-4" />
                        </button>
                      )}
                      {linked
                        ? canViewSpace && (
                            <Link
                              to={`/spaces/${linked.id}`}
                              title={`View space ${linked.key}`}
                              aria-label={`View space for ${deal.title}`}
                              className="rounded-md p-1.5 text-emerald-600 transition-colors hover:bg-emerald-50 dark:hover:bg-emerald-950/40"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Link>
                          )
                        : canCreateSpace && (
                            <button
                              type="button"
                              title="Create space / project"
                              aria-label={`Create space for ${deal.title}`}
                              disabled={creatingDealId === deal.id}
                              onClick={() => onCreateSpace(deal)}
                              className="rounded-md p-1.5 text-emerald-600 transition-colors hover:bg-emerald-50 disabled:opacity-50 dark:hover:bg-emerald-950/40"
                            >
                              {creatingDealId === deal.id ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
                            </button>
                          )}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>

        <div className="flex flex-col items-center justify-between gap-2 border-t border-border/50 px-4 py-2.5 text-xs text-muted-foreground sm:flex-row">
          <span>
            {paged.total === 0 ? 'No deals' : `Showing ${paged.from}–${paged.to} of ${paged.total} ${paged.total === 1 ? 'deal' : 'deals'}`}
          </span>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5">
              Rows
              <select
                aria-label="Rows per page"
                className={selectClass}
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value))
                  setPage(1)
                }}
              >
                {PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex items-center gap-1">
              <button
                type="button"
                aria-label="Previous page"
                disabled={paged.page <= 1}
                onClick={() => setPage(paged.page - 1)}
                className="rounded-md p-1 hover:bg-muted disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span>
                Page {paged.page} of {paged.pages}
              </span>
              <button
                type="button"
                aria-label="Next page"
                disabled={paged.page >= paged.pages}
                onClick={() => setPage(paged.page + 1)}
                className="rounded-md p-1 hover:bg-muted disabled:opacity-40"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={!!pending}
        title="Confirm bulk change"
        message={pendingText}
        confirmLabel="Apply"
        loading={busy}
        onCancel={() => setPending(null)}
        onConfirm={confirmPending}
      />
    </div>
  )
}
